// The SOURCE OF TRUTH for all UI strings (ADR-0032). Every other locale
// file is typed as `Messages = typeof en`, so adding a key here without
// translating it everywhere fails `npm run typecheck` — on purpose.
// `{name}`-style placeholders are filled by t(); keep them identical in
// every translation (guarded by tests/unit/i18n.test.ts).
export const en = {
  // Navigation / chrome
  navAdmin: 'Admin',
  navTemplates: 'Templates',
  navMenu: 'Menu',
  templatesNew: 'New template',
  templatesRepeat: 'Repeat',
  templatesEmpty: 'No templates yet.',
  templatesMultiplier: 'Payout multiplier',
  switchProfile: 'Switch profile',
  language: 'Language',
  close: 'Close',
  profileMenu: 'Your profile and settings',
  accessRequiredTitle: 'Family link required',
  accessRequiredBody: 'Open the private join link from your family admin on this browser. After that, this device stays authorized.',

  // Profile picker
  pickerWho: "Who's this?",
  pickerHi: 'Hi {name}',
  pickerEnterPin: 'Enter your PIN',
  pickerUnlock: 'Unlock',
  pickerBack: 'Back',
  pillAdmin: 'admin',
  pickerSharedDisplay: 'Family display',

  // Shared display (ADR-0049)
  displayTitle: 'Tasks',
  displayDone: 'Done — choose who did it',
  displayWhoTitle: 'Who did it?',
  displayWhoDidIt: '{title} — who did it?',
  displayWrongPin: 'Wrong PIN — try again',

  // Setup wizard
  setupWelcome: 'Welcome',
  setupIntro: 'One-time setup. Everything stays on this device/server — nothing leaves your home.',
  setupFamilyLabel: 'Household name',
  setupFamilyPlaceholder: 'e.g. The Novaks',
  setupAdminLabel: 'Your name (you become the first admin)',
  setupAdminPlaceholder: 'Your name',
  setupPinLabel: 'Your PIN',
  setupPinPlaceholder: 'Optional, 4+ digits',
  setupCreate: 'Create',
  setupHowStart: 'How do you want to start?',
  setupBlankTitle: 'Just let me do everything myself',
  setupBlankBlurb: 'Start empty. Add tasks, rewards, and rules on your own.',
  setupGuidedTitle: 'Guide me through presets',
  setupGuidedBlurb: 'Pick your household type; we pre-create sensible tags, rewards, and rules.',
  setupHousehold: 'What does your household look like?',
  setupHouseholdSingle: 'Just me',
  setupHouseholdSingleBlurb: 'Track your own tasks, reward yourself.',
  setupHouseholdCouple: 'Couple',
  setupHouseholdCoupleBlurb: 'Balance who does enough — fairly.',
  setupHouseholdFamily: 'Family with kids',
  setupHouseholdFamilyBlurb: 'Chores, homework, screen time, allowance.',
  setupHouseholdShared: 'Shared house',
  setupHouseholdSharedBlurb: 'Flatmates and the eternal cleaning rota.',
  setupBackArrow: '← Back',
  setupStep: 'Step {current} of {total}',
  setupQJobTitle: 'Do you want to track paid work?',
  setupQJobBody:
    'Logging work hours (say, an 8-hour shift) counts as busy time. The fairness view then expects less chore work from someone who just got home from work. This would add a "job" label for marking busy time.',
  setupQJobYes: 'Yes, track work',
  setupQJobNo: 'No, skip this',
  setupQSchoolTitle: 'Should school days be tracked?',
  setupQSchoolBody:
    'School hours count as busy time for kids — the same idea as work for adults. A school afternoon means less chores expected that day. This would add a "school" label.',
  setupQSchoolYes: 'Yes, track school',
  setupQSchoolNo: 'No, skip this',
  setupQHomeworkTitle: 'Are the kids homework-aged?',
  setupQHomeworkBody:
    'Homework tasks would earn visible credit on a personal board, but no pocket-money balance by default (you can change that later). This would add a "homework" label.',
  setupQHomeworkYes: 'Yes, add homework',
  setupQHomeworkNo: 'No, skip this',
  setupQKidApprovalTitle: "Should kids' spending need a parent's OK?",
  setupQKidApprovalBody:
    'Members in the "child" group would send spending requests to a parent for approval instead of spending directly. Changeable anytime under Admin → Permissions.',
  setupQKidApprovalYes: 'Yes, require approval',
  setupQKidApprovalNo: 'No, they spend freely',
  setupReview: 'Review',
  setupReviewIntro: 'Based on your answers, this will be created — nothing has been added yet:',
  setupReviewNothing: 'Nothing — everything you chose already exists.',
  setupReviewEditable: 'All of it stays editable under Admin.',
  setupCreateThese: 'Create these',
  setupPlanLabel: 'Label "{name}"',
  setupPlanReward: 'Reward: {title} ({cost} min)',
  setupPlanRule: 'Rule: {rule}',
  setupAllSet: 'All set!',
  setupCreatedForYou: 'Created for you (all editable in Admin):',
  setupNothingCreated: 'Everything was already in place.',
  setupSummaryTag: 'Tag "{name}"',
  setupSummaryReward: 'Reward "{name}"',
  setupNext: 'Next',
  setupAddMembersTitle: 'Add your family members now?',
  setupAddMembersBlurb:
    'Everyone who should earn and spend. PINs, admin rights, and groups can be set any time in Admin.',
  setupMemberPlaceholder: 'e.g. Ben',
  setupAddMemberBtn: 'Add',
  setupMembersDone: "Done — let's go",
  setupMembersAdmin: 'I’ll add them in Admin',

  // Tasks view
  tasksTitle: 'Tasks',
  tasksEmpty: 'Nothing to do — enjoy your free time.',
  tasksWontDo: 'Won’t do',
  tasksWontDoHint: 'Cancel this task — admins only',
  tasksNotNeeded: 'Not needed',
  tasksNotNeededHint: 'Close without pay — it just wasn’t needed today',
  tasksDone: 'Done',
  tasksMore: 'More options',
  tasksMoreHint: 'More options for this task',
  tasksCompleteFor: 'Complete for…',
  tasksDue: 'due {date}',
  tasksAddTitle: 'Add a task',
  tasksAddPlaceholder: 'e.g. Water the plants',
  composeNameLabel: 'Task name',
  tasksAdd: 'Add',
  tasksAdvanced: 'Advanced…',
  // Add-task composer (ADR-0042)
  composeWorth: 'Worth',
  composeMinSuffix: 'min',
  composeMore: 'More',
  composeLess: 'Less',
  composeFromTemplate: 'Create task from template',
  composeWhoCanDo: 'Who can do it',
  composeVisibility: 'Show it to',
  composeFollowupHeading: 'Follow-up tasks (spawn after this one is done)',
  composeFollowupAfter: 'after',
  composeFollowupTemplate: 'Choose a template…',
  composeFollowupAdd: '+ Add follow-up',
  composeFollowupRemove: 'Remove follow-up',
  composeAddTask: 'Add task',
  composeSaveTemplate: 'Save as template',
  composeTemplateHint: '· reuse it later without scheduling',

  // Full task builder (UI-16)
  builderTitle: 'Build a task',
  builderAccess: 'Access',
  builderTiming: 'Show only during',
  builderRepeatNote: 'To make it repeat, save it as a template — then set how often under Templates.',

  eligEveryone: 'Everyone',
  eligType: 'Type a name — kids, Ana…',
  eligGroupTag: 'group',
  eligNoMatches: 'No matches',
  eligRemove: 'Remove',
  tasksModalTitle: 'Task settings',
  tasksModalName: 'Name',
  tasksModalNamePlaceholder: 'Task name',
  tasksModalMinutes: 'Minutes (what it pays)',
  tasksModalRepeats: 'Repeats',
  tasksRepeatNone: 'does not repeat',
  tasksRepeatDaily: 'every day',
  tasksRepeatWeekly: 'every week',
  tasksRepeatMonthly: 'every month',
  tasksEvery: 'every',
  tasksUnitDays: 'day(s)',
  tasksUnitWeeks: 'week(s)',
  tasksUnitMonths: 'month(s)',
  tasksModalDue: 'Due date (one-off tasks only)',
  tasksModalVisibility: 'Shown to (keeps other lists tidy — not a secret)',
  tasksVisibilityEveryone: 'everyone',
  tasksVisibilityAssignee: 'only who it’s for (by eligibility)',
  tasksVisibilityAdmins: 'admins only',
  tasksModalLabels: 'Labels',
  tasksNoLabels: 'no labels yet',
  tasksModalEligibility: 'Who can do this (leave blank for everyone)',
  tasksEligibilityMembers: 'People',
  tasksEligibilityGroups: 'Groups',
  tasksNoGroups: 'no groups yet',
  tasksModalShowAt: 'Show at (hidden before this time each day)',
  tasksModalHideBy: 'Hide by (flagged as missed after this time, still completable)',
  tasksMissedLabel: 'Missed',
  tasksCancel: 'Cancel',
  tasksModalAdd: 'Add task',
  tasksNameMinutesRequired: 'Name and minutes are required.',
  sentForApproval: 'Sent to a parent for approval.',
  tasksHistoryTitle: 'Recently closed',
  statusDone: 'done',
  statusNotNeeded: 'not needed',
  statusExpired: 'expired',
  statusCancelled: 'cancelled',

  // Wallet
  walletTitle: 'Wallet',
  walletBalance: 'Spendable balance',
  walletRewards: 'Rewards',
  walletNoRewards: 'No rewards defined yet — an admin can add some.',
  walletBuy: 'Buy',
  walletFreeformTitle: 'Spend on something else',
  walletFreeformPlaceholder: 'What for? e.g. day off',
  walletSpend: 'Spend',
  walletHistory: 'History',

  // Leaderboard
  boardTitle: 'Leaderboard',
  boardWeek: 'Week',
  boardMonth: 'Month',
  boardRaw: 'Raw',
  boardFairness: 'Fairness',
  boardRange: '{from} → {to} (exclusive)',
  boardFamily: 'Family board',
  boardNamed: '{name} board',
  boardFairnessHint: 'Contribution relative to time actually available (after work/school).',

  // Today board
  todayTitle: 'Today',
  todayEmpty: 'Nothing on the board today.',
  // Tappable own chip (ADR-0058). The board stays wordless — this is the
  // accessible name of the chip-button, never printed on it.
  todayCompleteOwn: 'Mark “{title}” done',
  // Shared-display member switcher on the board (ADR-0061). The header cells
  // and the chips are icon/title-only, so these carry the meaning as
  // accessible names; only the banner strings are printed on screen.
  todayActAs: 'Do a task as {name}',
  todayActAsTitle: 'Who are you?',
  todayActingStop: 'Stop doing tasks as {name}',
  todayActingAs: 'Doing tasks as {name}',
  todayActingExit: 'Done',
  todayCompleteFor: 'Mark “{title}” done for {name}',

  // Admin
  policyActionLabel: 'Action',
  policyTargetLabel: 'Applies to',
  policyValueLabel: 'Rule',
  adminMemberLabel: 'Member',
  adminTagKind: 'Kind',
  adminTagSpending: 'Spending',
  adminRewardCost: 'Cost',
  adminCommittedDate: 'Date',
  adminCommittedMinutes: 'Busy time',
  adminAccessLinkLabel: 'Join link',
  walletSpendAmount: 'Minutes to spend',
  walletSpendNote: 'What for?',
  setupMemberNameLabel: 'Name',
  adminSectionsLabel: 'Settings sections',
  adminSectionPeople: 'People',
  adminSectionTasks: 'Tasks',
  adminSectionLedger: 'Ledger',
  adminSectionGeneral: 'General',
  adminSettingsTitle: 'Household settings',
  adminWeekStart: 'Leaderboard week starts on',
  adminWakingHours: 'Waking hours per day (fairness view)',
  adminSave: 'Save',
  adminSaved: 'Saved.',
  weekdayMonday: 'Monday',
  weekdayTuesday: 'Tuesday',
  weekdayWednesday: 'Wednesday',
  weekdayThursday: 'Thursday',
  weekdayFriday: 'Friday',
  weekdaySaturday: 'Saturday',
  weekdaySunday: 'Sunday',
  actionCreateAdhoc: 'Add a task',
  actionCreateRecurring: 'Add a repeating task',
  actionSpendFreeform: 'Spend (freeform)',
  actionSpendCatalog: 'Buy a reward',
  actionCommittedLog: 'Log busy time',
  approvalAddTask: '{name}: add task “{title}” ({minutes} min)',
  approvalAddRecurring: '{name}: add repeating task “{title}” ({minutes} min, {freq})',
  approvalSpend: '{name}: spend {minutes} min — {note}',
  approvalBuyReward: '{name}: buy reward #{id}',
  approvalCommitted: '{name}: log {minutes} min busy on {date}',
  approvalFallback: '{name}: {action}',
  adminApprovalsTitle: 'Waiting for approval',
  adminApprove: 'Approve',
  adminDeny: 'Deny',
  adminPermissionsTitle: 'Permissions',
  adminPermissionsHint:
    'Who may do what. Most specific rule wins: person beats group (tag) beats everyone. Admins always may.',
  policyAllowed: 'allowed',
  policyApproval: 'needs approval',
  policyDenied: 'denied',
  adminEveryone: 'Everyone',
  adminGroup: 'Group: {name}',
  adminMember: 'Member: {name}',
  adminNoRules: 'No rules set — everything is allowed for everyone.',
  adminRemove: 'Remove',
  adminSetRule: 'Set rule',
  adminMembersTitle: 'Members',
  adminMakeAdmin: 'Make admin',
  adminDemote: 'Demote',
  adminClearPin: 'Clear PIN',
  adminNamePlaceholder: 'Name',
  adminAvatarPlaceholder: 'icon (optional)',
  adminAddMember: 'Add member',
  adminOneTime: 'one-time',
  adminRetire: 'Retire',
  adminEdit: 'Edit',
  freqDaily: 'daily',
  freqWeekly: 'weekly',
  freqMonthly: 'monthly',
  adminAdd: 'Add',
  // Orphaned-task warnings (ADR-0043)
  adminOrphansTitle: 'Tasks nobody can do',
  adminOrphansIntro: "These recurring tasks are assigned only to inactive or removed members, so they never reach anyone's list. Reassign them (edit the task's who-can-do) or retire them.",
  adminOrphanReason: 'No active member can do this',
  deactivateOrphanWarning: 'Deactivating {name} would leave these tasks with nobody to do them: {titles}. Reassign them first, or deactivate anyway.',
  deactivateAnyway: 'Deactivate anyway',
  adminRewardsTitle: 'Rewards',
  adminRewardPlaceholder: 'e.g. 30 min screen time',
  adminTagsTitle: 'Tags',
  tagNotSpendable: 'not spendable',
  tagBoard: 'board: {name}',
  tagKindCategory: 'category',
  tagKindEligibility: 'eligibility',
  tagSpendable: 'spendable',
  adminTagPlaceholder: 'e.g. school',
  adminTagBoardPlaceholder: 'own board? (optional)',
  adminTagsHint:
    'Category tags classify tasks (rules apply). Eligibility tags gate who can do a task — assign them to members via the API for now.',
  adminFixTitle: 'Fix a mistake',
  adminFixHint:
    'Task not actually done? Reverse the earn. Spend was wrong? Undo it — the minutes come back. History stays either way.',
  adminNothingToCorrect: 'Nothing to correct yet.',
  adminEntryNumber: 'entry #{id}',
  adminReversedNote: 'reversed by admin',
  adminRefundedNote: 'refunded by admin',
  adminReverse: 'Reverse',
  adminUndoSpend: 'Undo spend',
  adminCommittedTitle: 'Log busy time (work/school)',
  adminCommittedHint: 'Feeds the fairness view: busy people are expected to do less.',
  adminCommittedPlaceholder: 'work / school…',
  adminLog: 'Log',
  adminAccessTitle: 'Family link',
  adminAccessDisabled: 'No public join link is active. The app behaves like a local trusted instance.',
  adminAccessEnabled: 'The join link is active. Browsers that open it stay authorized for {days} days, refreshed while they use the app.',
  adminAccessCreate: 'Create join link',
  adminAccessRotate: 'Rotate join link',
  adminAccessDisable: 'Disable join link',
  adminAccessLinkReady: 'Copy this link now; it is only shown after create/rotate.',
  adminCopy: 'Copy',
  adminCopied: 'Copied.',
  adminAccessCopyFallback: 'Link selected.',
};

export type Messages = typeof en;
